/**
 * Slugify a string for use as a URL-safe storefront slug.
 *
 * Supports Thai and other Unicode characters (like Shopify does).
 * Algorithm: lowercase → collapse whitespace/punctuation into hyphens → strip leading/trailing hyphens.
 *
 * Examples:
 *   "INGCO บล็อกไร้สาย 20 โวลต์" → "ingco-บล็อกไร้สาย-20-โวลต์"
 *   "My Product Name"             → "my-product-name"
 *   "Café & Résumé"               → "café-résumé"
 */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    // Replace any run of characters that are NOT: word chars (\w includes Unicode with u flag),
    // digits, or hyphens — with a single hyphen
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Decode a URL-encoded slug for human-readable display.
 * Handles truncated/partial encoding gracefully (e.g. slug cut off mid-byte
 * by a DB field length limit) by progressively stripping trailing %XX tokens
 * until decoding succeeds.
 *
 * Examples:
 *   "ingco-%e0%b8%9a%e0%b8%a5%e0%b9%87%e0%b8%ad%e0%b8%81" → "ingco-บล็อก"
 *   "ingco-%e0%b8%9a%e0%"                                   → "ingco-บ"
 *   "my-product-name"                                       → "my-product-name"
 */
export function displaySlug(slug: string): string {
  // First, strip any trailing incomplete token (bare "%" or "%X")
  let s = slug.replace(/%[0-9a-fA-F]?$/, "");

  // Try decoding, progressively stripping trailing %XX tokens on failure
  // (Thai chars are 3-byte UTF-8: %E0%B8%9A — truncation mid-char is invalid)
  for (let attempts = 0; attempts < 6; attempts++) {
    try {
      return decodeURIComponent(s);
    } catch {
      // Strip one more %XX token from the end and retry
      s = s.replace(/%[0-9a-fA-F]{2}$/, "");
    }
  }
  // All attempts failed — return with trailing hyphens cleaned
  return s.replace(/-+$/, "");
}
